/**
 * ProfilePicker — de tegels op een gedeeld apparaat.
 *
 * Toont ALLEEN naam, rol en avatar. Geen voortgang, geen streak, geen sterren.
 * Dat is geen bezuiniging maar een grens: zodra de kiezer prestaties toont,
 * wordt hij een vergelijkingsscherm tussen broers en zussen — didactisch
 * schadelijk, en een gegevensverstrekking waar niemand toestemming voor gaf.
 *
 * Een tegel logt NIET in. Hij vult het inlogveld voor; het wachtwoord blijft
 * vereist. Zie `deviceRoster.ts` voor waarom er geen sessies worden bewaard.
 *
 * @see docs/specs/PROFILE_SWITCHER_DESIGN.md
 */

import React from 'react';
import { motion } from 'framer-motion';
import { UserRound, GraduationCap, Heart, Shield, X, Plus } from 'lucide-react';
import type { RosterProfile } from '../../data/supabase/deviceRoster';
const ROLE_ICON = {
  learner: UserRound,
  parent: Heart,
  teacher: GraduationCap,
  admin: Shield
} as const;
const ROLE_LABEL = {
  learner: 'Leerling',
  parent: 'Ouder',
  teacher: 'Leerkracht',
  admin: 'Beheerder'
} as const;
interface ProfilePickerProps {
  profiles: RosterProfile[];
  onSelect: (profile: RosterProfile) => void;
  onSomeoneElse: () => void;
  onForget: (id: string) => void;
}
export function ProfilePicker({
  profiles,
  onSelect,
  onSomeoneElse,
  onForget
}: ProfilePickerProps) {
  return <section aria-labelledby="picker-heading" data-id="element-151" data-mp="2liw8w20q5c5l">
      <h2 id="picker-heading" className="text-xl font-black text-slate-900 mb-1" data-id="element-152" data-mp="2lk7bvzmcifvn">
        
        Wie ben jij?
      </h2>
      <p className="text-sm text-slate-500 mb-5" data-id="element-153" data-mp="3j7zfbbbzydbo">
        Kies je naam en vul daarna je wachtwoord in.
      </p>

      <ul className="grid grid-cols-2 gap-3" data-id="element-154" data-mp="39g1z1q64sv63">
        {profiles.map((profile, i) => {
        const Icon = ROLE_ICON[profile.role] ?? UserRound;
        return <motion.li key={profile.id} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.04,
          duration: 0.25
        }} className="relative" data-id="element-155" data-mp="34gpbt4bclm2z" data-mpi={i}>
              
              <button type="button" onClick={() => onSelect(profile)} className="w-full h-full p-4 rounded-2xl bg-white border-2 border-slate-200 hover:border-orange-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all text-left" data-id="element-156" data-mp="1v9f287b79h89">
                
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-3" data-id="element-157" data-mp="1qh3zkwp2rhc0">
                  {profile.avatar ? <img src={profile.avatar} alt="" className="w-12 h-12 rounded-full object-cover" data-id="element-158" data-mp="1ch345wrkx23x" /> : <Icon className="w-6 h-6 text-orange-600" data-id="element-159" data-mp="1gkivqeoskc9r" />}
                </div>
                <div className="font-black text-slate-900 truncate" data-id="element-160" data-mp="12k82j500ellf">
                  {profile.displayName}
                </div>
                <div className="text-xs font-semibold text-slate-400" data-id="element-161" data-mp="17jkto1xwyett">
                  {ROLE_LABEL[profile.role] ?? ''}
                </div>
              </button>

              {/* Een tegel moet weg kunnen zonder in de instellingen te duiken —
                   op een geleend apparaat wil je dat meteen kunnen. */}
              <button type="button" onClick={() => onForget(profile.id)} aria-label={`${profile.displayName} van dit apparaat halen`} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-300 flex items-center justify-center shadow-sm transition-colors" data-id="element-162" data-mp="q7cskhym69gx">
                
                <X className="w-3.5 h-3.5" data-id="element-163" data-mp="18yx4076m8174" />
              </button>
            </motion.li>;
      })}

        {/* Altijd aanwezig: wie zijn tegel niet herkent, moet gewoon kunnen
             inloggen. */}
        <li data-id="element-164" data-mp="1iwthh5n0z88e">
          <button type="button" onClick={onSomeoneElse} className="w-full h-full min-h-[7.5rem] p-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all text-left" data-id="element-165" data-mp="1ixmgtljnmvgi">
            
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-166" data-mp="3frpucpeoahm3">
              <Plus className="w-6 h-6 text-slate-500" data-id="element-167" data-mp="26sy5jr7mutnw" />
            </div>
            <div className="font-black text-slate-700" data-id="element-168" data-mp="2brrkzp6hcuqp">Iemand anders</div>
            <div className="text-xs font-semibold text-slate-400" data-id="element-169" data-mp="2grx60mi8vizt">Inloggen</div>
          </button>
        </li>
      </ul>
    </section>;
}